<template>
  <AnalyticsWidget
    title="Supplier Performance"
    subTitle="Bulk purchase orders received during the date ranges"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Purchase Order Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyPurchaseOrderFilter"
              @reset="resetPurchaseOrderFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingPurchaseOrderFilter.startDate"
                  :endDate="editingPurchaseOrderFilter.endDate"
                  @updateStartDate="editingPurchaseOrderFilter.startDate = $event"
                  @updateEndDate="editingPurchaseOrderFilter.endDate = $event"
                />
                <FormHorizontalRule />
                Supplier
                <ContactSearchableSelect placeholder="Supplier" :isCustomer="false" :isSupplier="true" :nullSelectionEnabled="true" nullSelectionName="No Supplier" v-model="editingPurchaseOrderFilter.contactId" />

              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedPurchaseOrderFilter.startDate | formatDate}} - {{appliedPurchaseOrderFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Purchase Orders Received" highlight>
              {{supplierSummaryData.purchaseOrderCount}}
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue :title="supplierSummaryData.averageDaysToRequired >= 0 ? 'Average Days Ahead' : 'Average Days Behind'">
              {{formatDecimal(Math.abs(supplierSummaryData.averageDaysToRequired), 2)}} days
              <template v-slot:info>Average days ahead/behind based on purchase orders received and when they were required</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="!hasSupplierFilter && hasChartData && hasMultipleSuppliers">
          <div class="col">
            <AnalyticsDashboardValue title="Best Performer">
              {{supplierSummaryData.bestSupplier.supplierName}}
              <template v-slot:info>With an average of {{formatDecimal(Math.abs(supplierSummaryData.bestSupplier.averageDaysToRequired), 2)}} days {{supplierSummaryData.bestSupplier.averageDaysToRequired >= 0 ? 'ahead' : 'behind'}} in {{supplierSummaryData.bestSupplier.totalPurchaseOrders}} purchase orders</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="!hasSupplierFilter && hasChartData && hasMultipleSuppliers">
          <div class="col">
            <AnalyticsDashboardValue title="Worst Performer">
              {{supplierSummaryData.worstSupplier.supplierName}}
              <template v-slot:info>With an average of {{formatDecimal(Math.abs(supplierSummaryData.worstSupplier.averageDaysToRequired), 2)}} days {{supplierSummaryData.worstSupplier.averageDaysToRequired >= 0 ? 'ahead' : 'behind'}} in {{supplierSummaryData.worstSupplier.totalPurchaseOrders}} purchase orders</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Purchase Orders Received"
          :series="supplierPOData.series"
          :xAxis="supplierPOData.xAxis"
          :yAxis="supplierPOData.yAxis"
          :legend="supplierPOData.legend"
          :options="supplierPOData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'
import ContactSearchableSelect from '@/modules/contacts/components/contactSearchableSelect'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'PurchasingSupplierPerformanceChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ContactSearchableSelect,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      supplierSummaryData: {
        purchaseOrderCount: 0,
        averageDaysToRequired: 0,
        bestSupplier: {
          supplierName: '',
          averageDaysToRequired: 0,
          totalPurchaseOrders: 0
        },
        worstSupplier: {
          supplierName: '',
          averageDaysToRequired: 0,
          totalPurchaseOrders: 0
        }
      },
      supplierPOList: [],
      supplierPOData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultPurchaseOrderFilter: {    
        startDate: moment().local().subtract(12, 'months').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        contactId: null
      },
      editingPurchaseOrderFilter: {},
      appliedPurchaseOrderFilter: {}
    }
  },
  computed: {
    hasSupplierFilter() {
      return !!this.appliedPurchaseOrderFilter.contactId
    },
    hasChartData() {
      return this.supplierPOData.series && this.supplierPOData.series.length > 0
    },
    hasMultipleSuppliers() {
      return this.supplierPOData.series && this.supplierPOData.series.length > 1
    }
  },
  mounted() {
    this.editingPurchaseOrderFilter = _.clone(this.defaultPurchaseOrderFilter)
    this.appliedPurchaseOrderFilter = _.clone(this.defaultPurchaseOrderFilter)

    this.requestChartData()
  },
  watch: {
  },
  methods: {
    applyPurchaseOrderFilter() {
      this.appliedPurchaseOrderFilter = _.clone(this.editingPurchaseOrderFilter)
      this.requestChartData()
    },
    resetPurchaseOrderFilter() {
      this.editingPurchaseOrderFilter = _.clone(this.defaultPurchaseOrderFilter)
      this.appliedPurchaseOrderFilter = _.clone(this.defaultPurchaseOrderFilter)

      this.requestChartData()
    },
    requestChartData() {
      var purchaseOrderFilter = {
        startDate: this.appliedPurchaseOrderFilter.startDate,
        endDate: this.appliedPurchaseOrderFilter.endDate,
        contactId: this.appliedPurchaseOrderFilter.contactId
      }

      this.supplierPOData.options = {
        aria: {
          enabled: true,
          decal: {
            show: true
          }
        }
      }

      this.$apiService.analyticsService.getPurchasingSupplierChartData(purchaseOrderFilter)
      .then(response => {
        let chartData = response.data

        let fullDateList = []
        chartData.forEach(supplierGroup => {

          supplierGroup.totalPurchaseOrders = supplierGroup.data.length
          supplierGroup.averageDaysToRequired = supplierGroup.data.reduce((cur, chartEntry) => cur + -chartEntry.daysToRequired, 0) / (supplierGroup.data.length || 1)

          supplierGroup.data.forEach(chartEntry => {
            chartEntry.dateStr = moment(chartEntry.dateReceivedClosed).local().format('DD MMM YYYY')
            fullDateList.push(chartEntry.dateStr)
          })
        })

        fullDateList = _.uniq(fullDateList).sort((a, b) => moment(a, 'DD MMM YYYY').toDate() - moment(b, 'DD MMM YYYY').toDate())

        this.supplierSummaryData.purchaseOrderCount = chartData.reduce((cur, supplierGroup) => cur + supplierGroup.data.length, 0)
        this.supplierSummaryData.averageDaysToRequired = chartData.reduce((cur, supplierGroup) => cur + supplierGroup.data.reduce((supplierCur, chartEntry) => supplierCur + -chartEntry.daysToRequired, 0), 0) / (this.supplierSummaryData.purchaseOrderCount || 1)

        // Best/Worst
        if(chartData.length > 0) {
          let bestSupplierGroup = _.maxBy(chartData, supplierGroup => supplierGroup.averageDaysToRequired)
          this.supplierSummaryData.bestSupplier.supplierName = bestSupplierGroup.supplierName
          this.supplierSummaryData.bestSupplier.averageDaysToRequired = bestSupplierGroup.averageDaysToRequired
          this.supplierSummaryData.bestSupplier.totalPurchaseOrders = bestSupplierGroup.totalPurchaseOrders

          let worstSupplierGroup = _.minBy(chartData, supplierGroup => supplierGroup.averageDaysToRequired)
          this.supplierSummaryData.worstSupplier.supplierName = worstSupplierGroup.supplierName
          this.supplierSummaryData.worstSupplier.averageDaysToRequired = worstSupplierGroup.averageDaysToRequired
          this.supplierSummaryData.worstSupplier.totalPurchaseOrders = worstSupplierGroup.totalPurchaseOrders
        }

        this.supplierPOData.legend = {}
        this.supplierPOData.xAxis = {
          data: fullDateList
        }
        this.supplierPOData.yAxis = [{
          type: 'value'
        }]

        chartData = chartData.filter(supplierGroup => supplierGroup.data.length > 0)

        this.supplierPOData.series = chartData.map(supplierGroup => {
          return {
            type: 'bar',
            name: supplierGroup.supplierName,
            emphasis: {
              focus: 'series'
            },
            stack: 'Purchase Orders Received',
            tooltip: {
              valueFormatter(value) {
                if(value < 0)
                  return `${Math.abs(value)} days behind`
                return `${value} days ahead`
              }
            },
            data: supplierGroup.data.map(chartEntry => {
              return [chartEntry.dateStr, -chartEntry.daysToRequired]
            })
          }
        })
      })
    },
  }
}
</script>